import { Link } from "react-router-dom";
import {
  ShieldCheck, Briefcase, ClipboardCheck, Handshake, Users, ArrowRight,
} from "lucide-react";

// Five-persona zone — surfaces "what you'll get" per visitor archetype.
// Order: Risk Manager (primary), GRC Consultant, Internal Auditor,
// Sales/BD Professional, Others.
const PERSONAS = [
  {
    id: "risk-manager",
    title: "The Risk Manager",
    badge: "Primary",
    badgeTint: "border-emerald-400/40 bg-emerald-500/10 text-emerald-300",
    accent: "emerald",
    Icon: ShieldCheck,
    profile: "CRO, Head of Risk or Senior Analyst at banks, NBFCs, insurers",
    pain: "Manually tracking RBI / SEBI / IRDAI circulars across 6 regulators — no single source of truth.",
    hooks: [
      { label: "India Regulatory Tracker", to: "/india/tracker" },
      { label: "Framework comparison", to: "/library" },
      { label: "DPDP Act checklist", to: "/india/digital-personal-data-protection-act-2023" },
    ],
    primary: { label: "Open India Hub", to: "/india" },
  },
  {
    id: "grc-consultant",
    title: "The GRC Consultant",
    badge: null,
    accent: "violet",
    Icon: Briefcase,
    profile: "Big 4 risk consultant, boutique advisory or independent practitioner",
    pain: "Needs client-ready templates, up-to-date framework summaries and credibility content for proposals.",
    hooks: [
      { label: "Template library", to: "/india/templates" },
      { label: "Framework atlas", to: "/library" },
      { label: "Co-branding & partners", to: "/partners" },
    ],
    primary: { label: "Browse the atlas", to: "/library" },
  },
  {
    id: "internal-auditor",
    title: "The Internal Auditor",
    badge: null,
    accent: "sky",
    Icon: ClipboardCheck,
    profile: "Internal Audit Head, CIA / CISA professional in BFSI",
    pain: "Building audit programs, mapping regs to audit scope, missing new framework alerts.",
    hooks: [
      { label: "Audit checklist tools", to: "/tools" },
      { label: "GRC maturity scorer", to: "/tools/grc-maturity" },
      { label: "Regulator alerts feed", to: "/india/alerts" },
    ],
    primary: { label: "Run a gap analysis", to: "/tools/grc-maturity" },
  },
  {
    id: "sales-bd",
    title: "Sales / BD Professional",
    badge: null,
    accent: "orange",
    Icon: Handshake,
    profile: "RM, Product Sales, GRC software seller at banks & fintechs",
    pain: "Needs quick, credible risk intelligence to speak the language of risk-aware buyers.",
    hooks: [
      { label: "Sales-trigger calendar", to: "/tools/sales-trigger-calendar" },
      { label: "Regulator one-pagers", to: "/press" },
      { label: "Weekly risk briefings", to: "/newsletters" },
    ],
    primary: { label: "Get the sales kit", to: "/tools/sales-trigger-calendar" },
  },
  {
    id: "others",
    title: "Curious explorers",
    badge: "Others",
    badgeTint: "border-rp-border bg-rp-bg text-rp-textDim",
    accent: "rose",
    Icon: Users,
    profile: "Students, researchers, journalists, fintech founders, risk-curious leaders",
    pain: "Want a guided tour of BFSI risk vocabulary, regulators and best practice without the consultant price tag.",
    hooks: [
      { label: "Glossary (212 terms)", to: "/glossary" },
      { label: "Free newsletters", to: "/newsletters" },
      { label: "Community forum", to: "/forum" },
    ],
    primary: { label: "Start the site guide", to: "/guide" },
  },
];

const ACCENT_RING = {
  emerald: "hover:border-emerald-400/60 hover:shadow-emerald-900/20",
  violet: "hover:border-violet-400/60 hover:shadow-violet-900/20",
  sky: "hover:border-sky-400/60 hover:shadow-sky-900/20",
  orange: "hover:border-orange-400/60 hover:shadow-orange-900/20",
  rose: "hover:border-rose-400/60 hover:shadow-rose-900/20",
};

const ACCENT_ICON = {
  emerald: "text-emerald-300",
  violet: "text-violet-300",
  sky: "text-sky-300",
  orange: "text-orange-300",
  rose: "text-rose-300",
};

const ACCENT_BTN = {
  emerald: "bg-emerald-500 hover:bg-emerald-400",
  violet: "bg-violet-500 hover:bg-violet-400",
  sky: "bg-sky-500 hover:bg-sky-400",
  orange: "bg-orange-500 hover:bg-orange-400",
  rose: "bg-rose-500 hover:bg-rose-400",
};

export default function PersonaZone() {
  return (
    <section
      className="border-b border-rp-border bg-gradient-to-b from-rp-bg to-rp-surface/30"
      data-testid="landing-persona-zone"
    >
      <div className="rp-container py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-rp-border bg-rp-surface px-3 py-1 text-[11px] font-mono uppercase tracking-[0.18em] text-rp-textDim">
            <Users className="h-3 w-3" /> Who is RiskPedia for
          </div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            One platform — <span className="text-emerald-400">five distinct journeys.</span>
          </h2>
          <p className="mt-4 text-rp-textDim text-base">
            Pick the path that matches your role. Each persona gets a tailored zone with the templates, trackers and intel that actually solve their daily pain — not generic content.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {PERSONAS.map((p) => {
            const Icon = p.Icon;
            return (
              <div
                key={p.id}
                className={`group relative rounded-2xl border border-rp-border bg-rp-surface p-6 transition-all hover:scale-[1.01] shadow-lg ${ACCENT_RING[p.accent]}`}
                data-testid={`persona-card-${p.id}`}
              >
                {p.badge && (
                  <div className={`absolute top-4 right-4 rounded-full border px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.18em] ${p.badgeTint || "border-rp-border bg-rp-bg text-rp-textDim"}`}>
                    {p.badge}
                  </div>
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div className={`h-10 w-10 rounded-xl border border-rp-border bg-rp-bg flex items-center justify-center ${ACCENT_ICON[p.accent]}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-rp-text">{p.title}</h3>
                </div>
                <p className="text-xs text-rp-textFaint mb-3 font-mono uppercase tracking-wide">{p.profile}</p>
                <p className="text-sm text-rp-textDim mb-4 leading-relaxed">
                  <span className="text-rp-text font-semibold">The pain:</span> {p.pain}
                </p>
                <div className="text-[10px] uppercase tracking-[0.18em] text-rp-textFaint mb-2 font-mono">
                  What they get
                </div>
                <ul className="space-y-1.5 mb-5">
                  {p.hooks.map((h) => (
                    <li key={h.to}>
                      <Link
                        to={h.to}
                        className="flex items-center justify-between gap-2 rounded-lg border border-rp-border bg-rp-bg px-3 py-2 text-xs text-rp-text hover:border-rp-textFaint transition-colors"
                        data-testid={`persona-${p.id}-hook-${h.to.replace(/[^a-z0-9]+/gi, "-")}`}
                      >
                        <span className="truncate">{h.label}</span>
                        <ArrowRight className="h-3 w-3 text-rp-textFaint shrink-0 group-hover:text-rp-text transition-colors" />
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to={p.primary.to}
                  className={`inline-flex items-center justify-center gap-1.5 rounded-full ${ACCENT_BTN[p.accent]} text-white text-xs font-semibold px-4 py-2 w-full transition-colors`}
                  data-testid={`persona-${p.id}-cta`}
                >
                  {p.primary.label} <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
